import React, { useState, useEffect, useContext } from 'react';
import {
    Container, Paper, Typography, Button, Box, TextField, FormControl,
    InputLabel, Select, MenuItem, Chip, Avatar, IconButton, Dialog,
    DialogTitle, DialogContent, DialogActions, Grid, Card, CardContent,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Alert, CircularProgress, Switch, FormControlLabel,
    useTheme, useMediaQuery, Collapse, InputAdornment
} from '@mui/material';
import {
    Search as SearchIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    AdminPanelSettings as AdminIcon,
    Block as BlockIcon,
    CheckCircle as ActiveIcon,
    Person as PersonIcon,
    SupervisorAccount as SuperAdminIcon,
    Close as CloseIcon,
    Save as SaveIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import userManagementService from '../../services/userManagementService';

const AdminUserManagement = () => {
    const { currentUser } = useContext(AuthContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

    // State management
    const [users, setUsers] = useState([]);
    const [userStats, setUserStats] = useState({
        total_users: 0,
        admin_users: 0,
        disabled_users: 0,
        recent_users: 0,
        users_with_assessments: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Dialog states
    const [viewDialog, setViewDialog] = useState(false);
    const [editDialog, setEditDialog] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [editingUser, setEditingUser] = useState(null);

    // Filter states
    const [filters, setFilters] = useState({
        search: '',
        is_admin: 'all',
        is_disabled: 'all'
    });
    const [showFilters, setShowFilters] = useState(false);

    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        if (currentUser?.is_admin) {
            loadUsers();
            loadUserStats();
        }
    }, [filters, page, rowsPerPage, currentUser]);

    const loadUsers = async () => {
        try {
            setLoading(true);
            setError('');

            const token = localStorage.getItem('accessToken');
            const params = {
                skip: page * rowsPerPage,
                limit: rowsPerPage,
                search: filters.search || undefined,
                is_admin: filters.is_admin !== 'all' ? filters.is_admin === 'true' : undefined
            };

            const data = await userManagementService.getUsers(params, token);
            setUsers(data);
        } catch (err) {
            setError('Failed to load users: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadUserStats = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const stats = await userManagementService.getUserStats(token);
            setUserStats(stats);
        } catch (err) {
            console.error('Failed to load user stats:', err);
        }
    };

    const handleEditUser = async () => {
        if (!editingUser) return;

        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');

            // Prepare update data (only send fields that changed)
            const updateData = {};
            if (editingUser.full_name !== selectedUser.full_name) {
                updateData.full_name = editingUser.full_name;
            }
            if (editingUser.email !== selectedUser.email) {
                updateData.email = editingUser.email;
            }
            if (editingUser.password && editingUser.password.trim()) {
                updateData.password = editingUser.password;
            }
            if (editingUser.is_admin !== selectedUser.is_admin) {
                updateData.is_admin = editingUser.is_admin;
            }
            if (editingUser.is_disabled !== selectedUser.is_disabled) {
                updateData.is_disabled = editingUser.is_disabled;
            }

            await userManagementService.updateUser(selectedUser.id, updateData, token);
            setSuccess('User updated successfully');
            loadUsers();
            setEditDialog(false);
            setEditingUser(null);
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Failed to update user: ' + err.message);
            setTimeout(() => setError(''), 3000);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleAdmin = async (userId, currentStatus) => {
        try {
            const token = localStorage.getItem('accessToken');
            await userManagementService.toggleAdminStatus(userId, token);
            setSuccess(`User admin status ${currentStatus ? 'removed' : 'granted'} successfully`);
            loadUsers();
            loadUserStats();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Failed to update admin status: ' + err.message);
            setTimeout(() => setError(''), 3000);
        }
    };

    const handleToggleDisabled = async (userId, currentStatus) => {
        try {
            const token = localStorage.getItem('accessToken');
            await userManagementService.toggleDisabledStatus(userId, token);
            setSuccess(`User ${currentStatus ? 'enabled' : 'disabled'} successfully`);
            loadUsers();
            loadUserStats();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Failed to update user status: ' + err.message);
            setTimeout(() => setError(''), 3000);
        }
    };

    const handleDeleteUser = async () => {
        if (!selectedUser) return;

        try {
            const token = localStorage.getItem('accessToken');
            await userManagementService.deleteUser(selectedUser.id, token);
            setSuccess('User deleted successfully');
            setUsers(users.filter(u => u.id !== selectedUser.id));
            loadUserStats();
            setDeleteDialog(false);
            setSelectedUser(null);
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Failed to delete user: ' + err.message);
            setTimeout(() => setError(''), 3000);
        }
    };

    const openEditDialog = (user) => {
        setSelectedUser(user);
        setEditingUser({
            ...user,
            password: '' // Don't pre-fill password
        });
        setEditDialog(true);
    };

    const getUserStatusChip = (user) => {
        if (user.is_disabled) {
            return (
                <Chip
                    icon={<BlockIcon />}
                    label="Disabled"
                    color="error"
                    size="small"
                    variant="outlined"
                />
            );
        }
        if (user.is_admin) {
            return (
                <Chip
                    icon={<AdminIcon />}
                    label="Admin"
                    color="primary"
                    size="small"
                />
            );
        }
        return (
            <Chip
                icon={<PersonIcon />}
                label="User"
                color="success"
                size="small"
                variant="outlined"
            />
        );
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Check admin access
    if (!currentUser?.is_admin) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error">
                    Access denied. Admin privileges required.
                </Alert>
            </Container>
        );
    }

    if (loading && users.length === 0) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                    <CircularProgress size={60} />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                mb: 3,
                gap: 2
            }}>
                <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 600 }}>
                    User Management
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<FilterIcon />}
                        onClick={() => setShowFilters(!showFilters)}
                        size={isMobile ? "small" : "medium"}
                    >
                        {isMobile ? 'Filter' : 'Filters'}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={loadUsers}
                        disabled={loading}
                        size={isMobile ? "small" : "medium"}
                    >
                        {isMobile ? '' : 'Refresh'}
                    </Button>
                </Box>
            </Box>

            {/* Alerts */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            {/* Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center', py: { xs: 1, sm: 2 } }}>
                            <Typography variant="h4" color="primary" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                                {userStats.total_users}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total Users
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center', py: { xs: 1, sm: 2 } }}>
                            <Typography variant="h4" color="secondary" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                                {userStats.admin_users}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Admins
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center', py: { xs: 1, sm: 2 } }}>
                            <Typography variant="h4" color="warning.main" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                                {userStats.disabled_users}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Disabled
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center', py: { xs: 1, sm: 2 } }}>
                            <Typography variant="h4" color="success.main" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                                {userStats.recent_users}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Recent
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filters */}
            <Collapse in={showFilters}>
                <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Filters
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                fullWidth
                                label="Search Users"
                                value={filters.search}
                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                placeholder="Name or email..."
                                size={isMobile ? "small" : "medium"}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                                <InputLabel>Admin Status</InputLabel>
                                <Select
                                    value={filters.is_admin}
                                    label="Admin Status"
                                    onChange={(e) => setFilters({ ...filters, is_admin: e.target.value })}
                                >
                                    <MenuItem value="all">All Users</MenuItem>
                                    <MenuItem value="true">Admins Only</MenuItem>
                                    <MenuItem value="false">Regular Users</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                                <InputLabel>Account Status</InputLabel>
                                <Select
                                    value={filters.is_disabled}
                                    label="Account Status"
                                    onChange={(e) => setFilters({ ...filters, is_disabled: e.target.value })}
                                >
                                    <MenuItem value="all">All Accounts</MenuItem>
                                    <MenuItem value="false">Active Only</MenuItem>
                                    <MenuItem value="true">Disabled Only</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                        <Button
                            variant="contained"
                            onClick={loadUsers}
                            disabled={loading}
                            size={isMobile ? "small" : "medium"}
                        >
                            Apply Filters
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => setFilters({ search: '', is_admin: 'all', is_disabled: 'all' })}
                            size={isMobile ? "small" : "medium"}
                        >
                            Clear
                        </Button>
                    </Box>
                </Paper>
            </Collapse>

            {/* Users Table */}
            <Paper elevation={2}>
                <TableContainer>
                    <Table size={isMobile ? "small" : "medium"}>
                        <TableHead>
                            <TableRow>
                                <TableCell>User</TableCell>
                                {!isMobile && <TableCell>Email</TableCell>}
                                <TableCell>Status</TableCell>
                                {!isMobile && <TableCell>Created</TableCell>}
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow key={user.id} hover>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar
                                                sx={{
                                                    bgcolor: user.is_admin ? 'primary.main' : 'secondary.main',
                                                    width: { xs: 32, sm: 40 },
                                                    height: { xs: 32, sm: 40 }
                                                }}
                                            >
                                                {user.is_admin ? <AdminIcon /> : <PersonIcon />}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                    {user.full_name}
                                                </Typography>
                                                {isMobile && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {user.email}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    {!isMobile && (
                                        <TableCell>
                                            <Typography variant="body2">
                                                {user.email}
                                            </Typography>
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        {getUserStatusChip(user)}
                                    </TableCell>
                                    {!isMobile && (
                                        <TableCell>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatDate(user.created_at)}
                                            </Typography>
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => { setSelectedUser(user); setViewDialog(true); }}
                                                title="View Details"
                                            >
                                                <ViewIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => openEditDialog(user)}
                                                title="Edit User"
                                                color="primary"
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            {user.id !== currentUser.id && (
                                                <>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleToggleAdmin(user.id, user.is_admin)}
                                                        title={user.is_admin ? "Remove Admin" : "Make Admin"}
                                                        color={user.is_admin ? "primary" : "default"}
                                                    >
                                                        <SuperAdminIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleToggleDisabled(user.id, user.is_disabled)}
                                                        title={user.is_disabled ? "Enable User" : "Disable User"}
                                                        color={user.is_disabled ? "error" : "default"}
                                                    >
                                                        {user.is_disabled ? <ActiveIcon fontSize="small" /> : <BlockIcon fontSize="small" />}
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => { setSelectedUser(user); setDeleteDialog(true); }}
                                                        title="Delete User"
                                                        color="error"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={userStats.total_users}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>

            {/* View User Dialog */}
            <Dialog
                open={viewDialog}
                onClose={() => setViewDialog(false)}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        User Details
                        <IconButton onClick={() => setViewDialog(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedUser && (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                    <Avatar sx={{ bgcolor: 'primary.main', width: 60, height: 60 }}>
                                        {selectedUser.is_admin ? <AdminIcon /> : <PersonIcon />}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h6">{selectedUser.full_name}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {selectedUser.email}
                                        </Typography>
                                        {getUserStatusChip(selectedUser)}
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    User ID
                                </Typography>
                                <Typography variant="body1">#{selectedUser.id}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Account Type
                                </Typography>
                                <Typography variant="body1">
                                    {selectedUser.is_admin ? 'Administrator' : 'Regular User'}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Created At
                                </Typography>
                                <Typography variant="body1">
                                    {formatDate(selectedUser.created_at)}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Last Updated
                                </Typography>
                                <Typography variant="body1">
                                    {formatDate(selectedUser.updated_at)}
                                </Typography>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
            </Dialog>

            {/* Edit User Dialog */}
            <Dialog
                open={editDialog}
                onClose={() => setEditDialog(false)}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        Edit User
                        <IconButton onClick={() => setEditDialog(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {editingUser && (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Full Name"
                                    value={editingUser.full_name}
                                    onChange={(e) => setEditingUser({ ...editingUser, full_name: e.target.value })}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    type="email"
                                    value={editingUser.email}
                                    onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="New Password (leave blank to keep current)"
                                    type="password"
                                    value={editingUser.password}
                                    onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })}
                                    helperText="Minimum 8 characters"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={editingUser.is_admin}
                                            onChange={(e) => setEditingUser({ ...editingUser, is_admin: e.target.checked })}
                                            disabled={editingUser.id === currentUser.id}
                                        />
                                    }
                                    label="Administrator privileges"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={editingUser.is_disabled}
                                            onChange={(e) => setEditingUser({ ...editingUser, is_disabled: e.target.checked })}
                                            disabled={editingUser.id === currentUser.id}
                                        />
                                    }
                                    label="Account disabled"
                                />
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialog(false)}>Cancel</Button>
                    <Button
                        onClick={handleEditUser}
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={loading}
                    >
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete User Dialog */}
            <Dialog
                open={deleteDialog}
                onClose={() => setDeleteDialog(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete user "{selectedUser?.full_name}"?
                        This action cannot be undone and may fail if the user has associated data.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
                    <Button
                        onClick={handleDeleteUser}
                        color="error"
                        variant="contained"
                        startIcon={<DeleteIcon />}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default AdminUserManagement;